import { Link } from 'react-router-dom';
import CaseStudyPageNav from '../../../components/feature/CaseStudyPageNav';
import CaseStudyNav from '../../../components/feature/CaseStudyNav';
import { useSEO } from '../../../hooks/useSEO';
import JsonLd from '../../../components/feature/JsonLd';

export default function MalariaNoMorePage() {
  useSEO({
    title: 'Malaria No More Campaign Strategy | Design For',
    description: 'Strategic consulting for global health campaigns. Research-driven strategy for malaria prevention and public health engagement.',
    keywords: 'malaria prevention, global health, public health campaigns, healthcare strategy, social impact',
    canonical: '/work/malaria-no-more',
    ogType: 'article',
    ogImage: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/ee9b8a579b0f0dfe46dd47075426190b.jpeg',
  });

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Strategic consulting for global health campaigns',
    description: 'We partnered with Malaria No More to develop strategic frameworks for malaria prevention campaigns, community engagement, and public health initiatives across affected regions.',
    url: 'https://example.com/work/malaria-no-more',
    author: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    about: 'Global health organization - Malaria prevention strategy',
    keywords: 'malaria prevention, global health, public health campaigns, healthcare strategy, social impact',
  };

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={jsonLdData} />
      <CaseStudyPageNav />

      {/* Main Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/work" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-10">
            <i className="ri-arrow-left-line"></i>
            Back to Work
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Text + Outcomes */}
            <div>
              <h1 className="text-4xl lg:text-5xl font-black text-black tracking-tight leading-tight mb-3">
                Ending the world&apos;s oldest and deadliest disease
              </h1>
              <h2 className="text-lg font-medium text-primary mb-8">Malaria No More</h2>

              <p className="text-base text-gray-600 leading-relaxed mb-4">
                We partnered with Malaria No More to develop digital tools and engagement strategies that support community health workers in malaria-endemic regions. Our work focused on creating accessible, culturally appropriate solutions for prevention, diagnosis, and treatment.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-10">
                The project included mobile applications for health workers, SMS-based reminder systems for medication adherence, and community education materials. Through human-centred design and close collaboration with local communities, we conducted extensive field research to ensure our solutions met real needs — tools now being used across multiple countries in Africa and Asia.
              </p>

              <h3 className="text-xl font-black text-black mb-5">Outcomes</h3>
              <ul className="space-y-3">
                {[
                  'Reached 500,000+ individuals through digital health tools',
                  'Deployed across 8 malaria-endemic countries in Africa and Asia',
                  'Trained and equipped 2,000+ community health workers',
                  'Developed culturally appropriate mobile applications for frontline health workers',
                  'Implemented SMS-based medication adherence reminder systems',
                  'Conducted extensive field research to validate solutions in real-world contexts',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base text-gray-700 leading-relaxed">
                    <span className="mt-2 w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Images */}
            <div className="flex flex-col gap-6">
              <div className="bg-gray-50 rounded-2xl flex items-center justify-center p-6 min-h-[340px]">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/a06f54a857c41d4cbe8f3fb37f4f8f71.jpeg"
                  alt="Malaria No More community health initiative"
                  className="max-w-full max-h-[420px] object-contain"
                />
              </div>
              <div className="bg-gray-50 rounded-2xl flex items-center justify-center p-6 min-h-[340px]">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/036a88b0a010d6946784d36ef7c17c9a.jpeg"
                  alt="Community health worker using digital malaria prevention tools"
                  className="max-w-full max-h-[420px] object-contain"
                />
              </div>
              <div className="bg-gray-50 rounded-2xl flex items-center justify-center p-6 min-h-[340px]">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/d12a5b9f2d856813c00e6c67b0d64b9f.jpeg"
                  alt="Malaria No More field research and community engagement"
                  className="max-w-full max-h-[420px] object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CaseStudyNav currentSlug="malaria-no-more" />

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png"
                alt="Design For"
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">Strategic consulting for ambitious businesses.</p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Navigate</h4>
              <div className="space-y-3">
                <Link to="/" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Home</Link>
                <Link to="/about" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">About</Link>
                <Link to="/services" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Services</Link>
                <Link to="/work" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Work</Link>
                <Link to="/resources" className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium">Insights</Link>
                <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Follow</h4>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</a>
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
